import { Kop, KOP } from "@/lib/kop";
import { QRCodeSVG } from "qrcode.react";
import { BIO_LABEL, pejabatRows, tglID } from "@/lib/suratTemplates";

const Rows = ({ rows }) => (
  <table className="my-2 ml-8 text-[15px]">
    <tbody>
      {rows.map(([k, val], i) => (
        <tr key={i} className="align-top">
          <td className="pr-3 py-[1px] whitespace-nowrap w-[230px]">{k}</td>
          <td className="pr-2">:</td>
          <td className="font-semibold">{val}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Anggota = ({ list }) => (
  <table className="w-full my-3 border-collapse text-[13px]" data-testid="surat-anggota-table">
    <thead>
      <tr>{["No", "Nama", "Umur", "Jenis Kelamin", "No. KTP", "Keterangan"].map((h) => <th key={h} className="border border-black px-2 py-1">{h}</th>)}</tr>
    </thead>
    <tbody>
      {(list.length ? list : [{}, {}, {}]).map((a, i) => (
        <tr key={i}>
          <td className="border border-black px-2 py-1 text-center">{i + 1}</td>
          <td className="border border-black px-2 py-1">{a.nama || ""}</td>
          <td className="border border-black px-2 py-1 text-center">{a.usia != null ? `${a.usia} th` : ""}</td>
          <td className="border border-black px-2 py-1">{a.jenis_kelamin || ""}</td>
          <td className="border border-black px-2 py-1">{a.nik || ""}</td>
          <td className="border border-black px-2 py-1">{a.shdk || ""}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const Ttd = ({ title, sub, nama, nip, pangkat, img }) => (
  <div className="text-center w-[280px]">
    <div>{title}</div>
    {sub && <div className="font-semibold">{sub}</div>}
    {img ? <img src={img} alt="Tanda tangan elektronik" className="h-20 mx-auto object-contain" data-testid="ttd-image" /> : <div className="h-20" />}
    <div className="font-bold underline">{nama}</div>
    {pangkat && <div>{pangkat}</div>}
    {nip !== undefined && <div>NIP. {nip}</div>}
  </div>
);

const Signature = ({ sign, bio, pj }) => {
  const lurah = <Ttd title={sign === "lurah" ? "" : "Mengetahui :"} sub={sign === "an_lurah" ? `A.n ${pj.jabatan}` : pj.jabatan} nama={sign === "an_lurah" ? "(………………………………)" : pj.nama} pangkat={sign === "an_lurah" ? "" : pj.pangkat} nip={sign === "an_lurah" ? "……………………" : pj.nip} img={sign === "an_lurah" ? "" : pj.ttd} />;
  if (sign === "pemohon") return <div className="flex justify-between mt-4">{<Ttd title="" sub="Pemohon" nama={`( ${bio.nama} )`} />}{lurah}</div>;
  if (sign === "distrik") return <div className="flex justify-between mt-4"><Ttd title="Mengetahui :" sub="Kepala Distrik Wania" nama={pj.distrik_nama || DOTS_NAMA} nip={pj.distrik_nip || DOTS_NIP} />{lurah}</div>;
  return <div className="flex justify-end mt-4">{lurah}</div>;
};

const Verif = ({ id, pj }) => (
  <div className="mt-8 flex items-center gap-3 border border-black/60 p-2 w-fit text-[11px] leading-snug" data-testid="surat-qr">
    <QRCodeSVG value={`${window.location.origin}/verifikasi/${id}`} size={70} />
    <div className="max-w-[300px]">
      <div className="font-bold">Ditandatangani secara elektronik</div>
      <div>oleh {pj.jabatan}, {pj.nama}.</div>
      <div>Pindai QR untuk memverifikasi keaslian surat melalui sistem TAPARU INAUGA.</div>
    </div>
  </div>
);

const DOTS_NAMA = "(……………………………)";
const DOTS_NIP = "……………………";

export const SuratDoc = ({ tpl, bio, extra, nomor, tanggal, pejabat, suratId }) => {
  const bioRows = (keys) => keys.map((k) => [BIO_LABEL[k], bio[k] || "……………"]);
  const extraRows = (rows = []) => rows.map(([l, k]) => [l, k === "berangkat" ? tglID(extra[k]) : extra[k] || "……………"]);
  const rows = [...bioRows(tpl.bio), ...extraRows(tpl.extraRows), ...bioRows(tpl.bioAfter || []), ...extraRows(tpl.extraRowsEnd)];
  return (
    <div className="bg-white text-black w-[210mm] min-h-[297mm] px-[20mm] py-[14mm] text-[15px] leading-relaxed" style={{ fontFamily: "'Times New Roman', serif" }} data-testid="surat-doc">
      <Kop />
      <div className="text-center mt-5">
        <div className="font-bold underline uppercase text-[17px]" data-testid="surat-judul">{tpl.judul}</div>
        <div data-testid="surat-nomor">Nomor : {nomor}</div>
      </div>
      <p className="mt-5">{tpl.pejabat ? "Yang bertanda tangan di bawah ini :" : tpl.intro || "Kepala Kelurahan Inauga dengan ini menerangkan bahwa :"}</p>
      {tpl.pejabat && <Rows rows={pejabatRows(pejabat)} />}
      {rows.length > 0 && <Rows rows={rows} />}
      {tpl.anggota && <Anggota list={bio.anggota || []} />}
      {!tpl.pernyataan && !tpl.pejabat && <p className="font-semibold mt-2">Keterangan :</p>}
      <div className="space-y-2 mt-1">
        {tpl.body(bio, extra).map((b, i) =>
          typeof b === "string" ? <p key={i} className="text-justify indent-8">{b}</p> : <Rows key={i} rows={b.rows} />,
        )}
      </div>
      <div className="flex justify-end mt-6">
        <table className="text-[15px]"><tbody>
          <tr><td className="pr-3">Dikeluarkan di</td><td className="pr-2">:</td><td>{KOP.tempat}</td></tr>
          <tr><td className="pr-3">Pada tanggal</td><td className="pr-2">:</td><td data-testid="surat-tanggal">{tglID(tanggal)}</td></tr>
        </tbody></table>
      </div>
      <Signature sign={tpl.sign} bio={bio} pj={pejabat} />
      {suratId && pejabat.qr !== false && <Verif id={suratId} pj={pejabat} />}
    </div>
  );
};
